<template>
  <v-main>
    <v-container fluid>
      <!-- Header -->
      <v-row>
        <v-col md="12">
          <v-card elevation="6" rounded="xxl">
            <v-card-title>
              <h3 class="font-weight-bold">App Version Control</h3>
              <v-spacer></v-spacer>
              <v-chip color="primary" small class="mr-2">
                <v-icon left small>mdi-information-outline</v-icon>
                Push a new version to prompt users to update
              </v-chip>
            </v-card-title>
          </v-card>
        </v-col>
      </v-row>

      <!-- Loading -->
      <v-row v-if="isLoading">
        <v-col md="12" class="text-center py-10">
          <v-progress-circular indeterminate color="primary" size="48"></v-progress-circular>
        </v-col>
      </v-row>

      <!-- Platform Cards -->
      <v-row v-else class="mt-2" justify="center">
        <!-- Android Card -->
        <v-col md="5" sm="12">
          <v-card elevation="6" rounded="xxl">
            <v-card-text class="pb-0">
              <div class="d-flex align-center mb-4">
                <v-avatar color="green lighten-5" size="52">
                  <v-icon color="green darken-2" size="30">mdi-android</v-icon>
                </v-avatar>
                <div class="ml-4">
                  <div class="text-h6 font-weight-bold">Android</div>
                  <div class="text-caption grey--text">Google Play Store</div>
                </div>
                <v-spacer></v-spacer>
                <v-chip color="green" text-color="white">
                  <v-icon left small>mdi-tag-outline</v-icon>
                  {{ android ? android.version : 'Not set' }}
                </v-chip>
              </div>
              <v-divider></v-divider>
              <div class="py-3">
                <div class="d-flex justify-space-between align-center mb-2">
                  <span class="grey--text text--darken-1">Force Update</span>
                  <v-chip
                    :color="android && android.force_update ? 'red' : 'grey'"
                    text-color="white"
                    small
                  >
                    {{ android && android.force_update ? 'Enabled' : 'Disabled' }}
                  </v-chip>
                </div>
                <div class="d-flex justify-space-between align-center">
                  <span class="grey--text text--darken-1">Last Updated</span>
                  <span class="text-body-2">{{ android ? formatDate(android.created_at) : '-' }}</span>
                </div>
              </div>
            </v-card-text>
            <v-card-actions class="px-4 pb-4">
              <v-btn v-can:add="'versions'" color="green darken-1" dark rounded block @click="openUpdateDialog('android')">
                <v-icon left>mdi-upload</v-icon>
                Push Android Version
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-col>

        <!-- iOS Card -->
        <v-col md="5" sm="12">
          <v-card elevation="6" rounded="xxl">
            <v-card-text class="pb-0">
              <div class="d-flex align-center mb-4">
                <v-avatar color="blue lighten-5" size="52">
                  <v-icon color="blue darken-2" size="30">mdi-apple</v-icon>
                </v-avatar>
                <div class="ml-4">
                  <div class="text-h6 font-weight-bold">iOS</div>
                  <div class="text-caption grey--text">Apple App Store</div>
                </div>
                <v-spacer></v-spacer>
                <v-chip color="blue" text-color="white">
                  <v-icon left small>mdi-tag-outline</v-icon>
                  {{ ios ? ios.version : 'Not set' }}
                </v-chip>
              </div>
              <v-divider></v-divider>
              <div class="py-3">
                <div class="d-flex justify-space-between align-center mb-2">
                  <span class="grey--text text--darken-1">Force Update</span>
                  <v-chip
                    :color="ios && ios.force_update ? 'red' : 'grey'"
                    text-color="white"
                    small
                  >
                    {{ ios && ios.force_update ? 'Enabled' : 'Disabled' }}
                  </v-chip>
                </div>
                <div class="d-flex justify-space-between align-center">
                  <span class="grey--text text--darken-1">Last Updated</span>
                  <span class="text-body-2">{{ ios ? formatDate(ios.created_at) : '-' }}</span>
                </div>
              </div>
            </v-card-text>
            <v-card-actions class="px-4 pb-4">
              <v-btn v-can:add="'versions'" color="blue darken-1" dark rounded block @click="openUpdateDialog('ios')">
                <v-icon left>mdi-upload</v-icon>
                Push iOS Version
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>

      <!-- Version History -->
      <v-row class="mt-2" v-if="!isLoading">
        <v-col md="12">
          <v-card elevation="6" rounded="xxl">
            <v-card-title>
              <h4 class="font-weight-bold">Version History</h4>
            </v-card-title>
            <v-card-text>
              <v-simple-table>
                <template v-slot:default>
                  <thead>
                    <tr>
                      <th class="text-left">Sr No</th>
                      <th class="text-left">Platform</th>
                      <th class="text-left">Version</th>
                      <th class="text-left">Force Update</th>
                      <th class="text-left">Created At</th>
                    </tr>
                  </thead>
                  <tbody v-if="history.length > 0">
                    <tr v-for="(item, i) in history" :key="i">
                      <td>{{ i + 1 }}</td>
                      <td>
                        <v-chip
                          small
                          :color="item.platform === 'android' ? 'green' : 'blue'"
                          text-color="white"
                        >
                          <v-icon left small>{{ item.platform === 'android' ? 'mdi-android' : 'mdi-apple' }}</v-icon>
                          {{ item.platform === 'android' ? 'Android' : 'iOS' }}
                        </v-chip>
                      </td>
                      <td>
                        <strong>{{ item.version }}</strong>
                      </td>
                      <td>
                        <v-chip
                          small
                          :color="item.force_update ? 'red' : 'grey lighten-1'"
                          :text-color="item.force_update ? 'white' : 'grey darken-2'"
                        >
                          {{ item.force_update ? 'Yes' : 'No' }}
                        </v-chip>
                      </td>
                      <td>{{ formatDate(item.created_at) }}</td>
                    </tr>
                  </tbody>
                  <tbody v-else>
                    <tr>
                      <td colspan="5" class="text-center grey--text py-4">No version history found</td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>

    <!-- Update Version Dialog -->
    <v-dialog v-model="updateDialog" max-width="480" persistent>
      <v-card rounded="xl">
        <v-card-title class="d-flex align-center">
          <v-icon :color="dialogPlatform === 'android' ? 'green darken-2' : 'blue darken-2'" class="mr-2">
            {{ dialogPlatform === 'android' ? 'mdi-android' : 'mdi-apple' }}
          </v-icon>
          Push {{ dialogPlatform === 'android' ? 'Android' : 'iOS' }} Version
        </v-card-title>
        <v-card-text>
          <v-alert
            :type="dialogPlatform === 'android' ? 'success' : 'info'"
            dense
            text
            class="mb-4"
          >
            This will only update the <strong>{{ dialogPlatform === 'android' ? 'Android' : 'iOS' }}</strong> version.
            The other platform will not be affected.
          </v-alert>

          <v-text-field
            v-model="form.version"
            label="New Version Number"
            placeholder="e.g. 1.0.5"
            outlined
            rounded
            :error-messages="formErrors.version"
            :prepend-inner-icon="dialogPlatform === 'android' ? 'mdi-android' : 'mdi-apple'"
            hint="Example: 1.0.0, 2.1.3"
            persistent-hint
          ></v-text-field>

          <div class="mt-4 mb-2">
            <v-switch
              v-model="form.force_update"
              :label="form.force_update ? 'Force Update: ON — Users must update before continuing' : 'Force Update: OFF — Update is optional'"
              :color="form.force_update ? 'red' : 'grey'"
              inset
            ></v-switch>
          </div>

          <v-alert
            v-if="form.force_update"
            type="warning"
            dense
            text
            class="mt-2"
          >
            <strong>Warning:</strong> Force update will block users from using the app until they update.
          </v-alert>
        </v-card-text>
        <v-card-actions class="px-4 pb-4">
          <v-btn rounded outlined @click="closeDialog" :disabled="isSaving">Cancel</v-btn>
          <v-spacer></v-spacer>
          <v-btn v-can:edit="'versions'"
            :color="dialogPlatform === 'android' ? 'green darken-1' : 'blue darken-1'"
            dark
            rounded
            @click="saveVersion"
            :loading="isSaving"
          >
            <v-icon left>mdi-content-save</v-icon>
            Save Version
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Success Dialog -->
    <v-dialog v-model="successDialog" width="420">
      <v-card rounded="xxl">
        <v-container>
          <v-row align="center">
            <v-col sm="3" align="center">
              <v-icon style="color: green; font-size: 70px">mdi-check-circle-outline</v-icon>
            </v-col>
            <v-col sm="9" align="center">
              <v-card-text>
                <h2 style="color: green">Done!</h2>
                <br />
                <p>{{ dialogPlatform === 'android' ? 'Android' : 'iOS' }} version updated. The app will now prompt {{ dialogPlatform === 'android' ? 'Android' : 'iOS' }} users to update.</p>
                <v-btn color="success" @click="successDialog = false">OK</v-btn>
              </v-card-text>
            </v-col>
          </v-row>
        </v-container>
      </v-card>
    </v-dialog>
  </v-main>
</template>

<script>
import axios from "axios";
export default {
  name: "VersionsPage",
  data() {
    return {
      android: null,
      ios: null,
      history: [],
      isLoading: false,
      isSaving: false,
      updateDialog: false,
      successDialog: false,
      dialogPlatform: "android", // 'android' or 'ios'
      form: {
        version: "",
        force_update: false,
      },
      formErrors: {
        version: "",
      },
    };
  },
  mounted() {
    this.getData();
  },
  methods: {
    async getData() {
      this.isLoading = true;
      try {
        const latestRes = await axios.get("versions?search=latest");
        this.android = latestRes.data.data.android || null;
        this.ios = latestRes.data.data.ios || null;

        const historyRes = await axios.get("versions");
        this.history = historyRes.data.data || [];
      } catch (error) {
        console.error(error);
        this.isUnAuthorised();
      }
      this.isLoading = false;
    },

    openUpdateDialog(platform) {
      this.dialogPlatform = platform;
      const current = platform === "android" ? this.android : this.ios;
      this.form.version = current ? current.version : "";
      this.form.force_update = current ? !!current.force_update : false;
      this.formErrors.version = "";
      this.updateDialog = true;
    },

    closeDialog() {
      this.updateDialog = false;
      this.form.version = "";
      this.form.force_update = false;
      this.formErrors.version = "";
    },

    async saveVersion() {
      this.formErrors.version = "";
      if (!this.form.version || !this.form.version.trim()) {
        this.formErrors.version = "Version number is required.";
        return;
      }

      this.isSaving = true;
      try {
        await axios.post("versions", {
          version: this.form.version.trim(),
          platform: this.dialogPlatform,
          force_update: this.form.force_update,
        });
        this.updateDialog = false;
        this.successDialog = true;
        await this.getData();
      } catch (error) {
        console.error(error);
        if (error.response && error.response.data && error.response.data.errors) {
          const errors = error.response.data.errors;
          this.formErrors.version = errors.version ? errors.version[0] : "";
        }
      }
      this.isSaving = false;
    },

    formatDate(dateString) {
      if (!dateString) return "-";
      const date = new Date(dateString);
      return date.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    },
  },
};
</script>
